import type {
  DigestHighlightDto,
  DigestItemDto,
  SportDigestGroupDto,
} from '@nomiss/sdk';
import { ApiRequestError, executeSdkRawRequest } from './api-request.util';
import { digestApi, resolveAccessToken } from './sdk-client';

export type DigestHighlight = DigestHighlightDto;
export type DigestItem = DigestItemDto;
export type SportDigestGroup = Omit<SportDigestGroupDto, 'code'> & { code?: string };

export type DigestFeedParams = {
  page?: number;
  limit?: number;
  accessToken?: string | null;
};

function authInitOverrides(
  accessToken?: string | null,
): RequestInit | undefined {
  const token = accessToken ?? resolveAccessToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
}

function normalizeHighlight(raw: DigestHighlightDto): DigestHighlight {
  const highlight: DigestHighlight = {
    url: raw.url,
    streamingPlatform: raw.streamingPlatform,
  };

  if (raw.thumbnailUrl !== undefined) {
    highlight.thumbnailUrl = raw.thumbnailUrl;
  }

  if (raw.durationSeconds !== undefined) {
    highlight.durationSeconds = raw.durationSeconds;
  }

  return highlight;
}

function normalizeDigestItem(raw: DigestItemDto): DigestItem {
  const item: DigestItem = {
    id: raw.id,
    title: raw.title,
    scores: raw.scores,
    matchDate: raw.matchDate,
    isFavourited: Boolean(raw.isFavourited),
    highlights: {
      serp: (raw.highlights?.serp ?? []).map(normalizeHighlight),
      youtube: (raw.highlights?.youtube ?? []).map(normalizeHighlight),
    },
  };

  if (raw.thumbnailUrl !== undefined) {
    item.thumbnailUrl = raw.thumbnailUrl;
  }

  if (raw.streamingPlatform !== undefined) {
    item.streamingPlatform = raw.streamingPlatform;
  }

  return item;
}

function normalizeSportGroups(raw: SportDigestGroupDto[] | null | undefined): SportDigestGroup[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map(group => {
    const code = (group as SportDigestGroupDto & { code?: unknown }).code;
    return {
      id: group.id,
      name: group.name,
      ...(typeof code === 'string' ? { code } : {}),
      digests: (group.digests ?? []).map(normalizeDigestItem),
    };
  });
}

export async function getDigestFeed(
  params: DigestFeedParams = {},
): Promise<SportDigestGroup[]> {
  const initOverrides = authInitOverrides(params.accessToken);

  const data = await executeSdkRawRequest<SportDigestGroupDto[]>({
    request: () =>
      digestApi.digestControllerGetFeedV1Raw(
        {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        },
        initOverrides,
      ),
    fallback: 'Digest feed API failed',
  });

  return normalizeSportGroups(data);
}

export async function getDigestUpcoming(
  params: DigestFeedParams = {},
): Promise<SportDigestGroup[]> {
  const initOverrides = authInitOverrides(params.accessToken);

  const data = await executeSdkRawRequest<SportDigestGroupDto[]>({
    request: () =>
      digestApi.digestControllerGetUpcomingV1Raw(
        {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        },
        initOverrides,
      ),
    fallback: 'Digest upcoming API failed',
  });

  return normalizeSportGroups(data);
}

export async function getDigestMissed(
  params: DigestFeedParams = {},
): Promise<SportDigestGroup[]> {
  const initOverrides = authInitOverrides(params.accessToken);

  const data = await executeSdkRawRequest<SportDigestGroupDto[]>({
    request: () =>
      digestApi.digestControllerGetMissedV1Raw(
        {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        },
        initOverrides,
      ),
    fallback: 'Digest missed API failed',
  });

  return normalizeSportGroups(data);
}

export async function getDigestWatched(
  params: DigestFeedParams = {},
): Promise<SportDigestGroup[]> {
  const initOverrides = authInitOverrides(params.accessToken);

  const data = await executeSdkRawRequest<SportDigestGroupDto[]>({
    request: () =>
      digestApi.digestControllerGetWatchedV1Raw(
        {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        },
        initOverrides,
      ),
    fallback: 'Digest watched API failed',
  });

  return normalizeSportGroups(data);
}

export async function getDigestFavourite(
  params: DigestFeedParams = {},
): Promise<SportDigestGroup[]> {
  const initOverrides = authInitOverrides(params.accessToken);

  const data = await executeSdkRawRequest<SportDigestGroupDto[]>({
    request: () =>
      digestApi.digestControllerGetFavouriteV1Raw(
        {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        },
        initOverrides,
      ),
    fallback: 'Digest favourite API failed',
  });

  return normalizeSportGroups(data);
}

export type DigestSport = {
  id: string;
  name: string;
};

export async function getDigestSports(
  accessToken?: string | null,
): Promise<DigestSport[]> {
  const initOverrides = authInitOverrides(accessToken);

  const data = await executeSdkRawRequest<DigestSport[]>({
    request: () => digestApi.digestControllerGetSportsV1Raw(initOverrides),
    fallback: 'Digest sports API failed',
  });

  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .filter(
      (sport): sport is DigestSport =>
        Boolean(sport) &&
        typeof sport.id === 'string' &&
        typeof sport.name === 'string',
    )
    .map(sport => ({ id: sport.id, name: sport.name }));
}

export async function markDigestWatched(
  digestId: string,
  accessToken?: string | null,
): Promise<void> {
  const initOverrides = authInitOverrides(accessToken);

  await executeSdkRawRequest<null>({
    request: () =>
      digestApi.digestControllerMarkAsWatchedV1Raw({ digestId }, initOverrides),
    fallback: 'Mark digest watched API failed',
  });
}

export async function reportDigestBrokenLink(
  digestId: string,
  brokenLink: string,
  accessToken?: string | null,
): Promise<void> {
  const initOverrides = authInitOverrides(accessToken);

  await executeSdkRawRequest<null>({
    request: () =>
      digestApi.digestControllerReportBrokenLinkV1Raw(
        {
          digestId,
          reportBrokenLinkDto: { brokenLink },
        },
        initOverrides,
      ),
    fallback: 'Report broken link API failed',
  });
}

export async function addDigestFavourite(
  digestId: string,
  accessToken?: string | null,
): Promise<void> {
  const initOverrides = authInitOverrides(accessToken);

  await executeSdkRawRequest<null>({
    request: () =>
      digestApi.digestControllerAddFavouriteV1Raw({ digestId }, initOverrides),
    fallback: 'Add digest favourite API failed',
  });
}

export async function removeDigestFavourite(
  digestId: string,
  accessToken?: string | null,
): Promise<void> {
  const initOverrides = authInitOverrides(accessToken);

  await executeSdkRawRequest<null>({
    request: () =>
      digestApi.digestControllerRemoveFavouriteV1Raw({ digestId }, initOverrides),
    fallback: 'Remove digest favourite API failed',
  });
}

export class DigestFeedError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'DigestFeedError');
  }
}
